import moment from 'moment-timezone';
import React, { type FC, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Header, Icon, Message, Portal, Segment } from 'semantic-ui-react';
import { Field, Form as FinalForm, useForm } from 'react-final-form';
import { EntityDescriptions } from 'src/entities/description';
import { Projection } from 'src/entities/description/EntityDescription';
import CompanyInvoiceFull from 'src/entities/invoice/company/CompanyInvoiceFull';
import { createMonthMenuItems } from 'src/helper/helper';
import { fetchEntities } from 'src/redux/actions';
import { getEntities } from 'src/redux/selectors';
import {  StoreState } from 'src/types';
import Company from '@/entities/companies/Company';
import { DateTimeInput } from 'src/components/elements/form-inputs/DateTimeInput';
import { SimpleInput } from '@/components/elements/form-inputs/SimpleInput';
import { SelectField } from '@/components/elements/form-inputs/SelectField';
import callApi, { HttpMethod } from '@/services/api';
import { ENV } from '@/config/env';
import SupportHoursInvoiceItem, {
    SupportHoursInvoiceItemFormFields,
} from '@/entities/support-hours/SupportHoursInvoiceItem';
import DataTable, { DataTableCol } from '@/components/elements/datatable/DataTable';
import { useDisclosure } from '@/hooks/useDisclosure';
import { Dialog } from '@/components/elements/dialog';

const SupportHoursFetcher: FC<{
    company?: number;
    month?: string;
    onFetched: (data: SupportHoursInvoiceItem[]) => void
}> = React.memo(({ company, month, onFetched }) => {
    useEffect(() => {
        if (company && month && month !== '-') {
            callApi(
                `${ENV.apiUrl}support-hours-invoice-items/getOperatorSupportInvoiceItems?companyId=${company}&monthYear=${month}`,
                HttpMethod.GET,
            ).then(result => {
                onFetched(result?.response || []);
            });
        }
    }, [company, month, onFetched]);

    return null;
});

export const SupportHoursAdministration: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [supportHours, setSupportHours] = React.useState<SupportHoursInvoiceItem[] | undefined>(undefined);
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [showError,setShowError] =  React.useState(false)

    const [selectedSupportHourInvoiceItem, setSelectedSupportHourInvoiceItem] = React.useState<SupportHoursInvoiceItem | undefined>(undefined)
    useEffect(() => {
        dispatch(fetchEntities(EntityDescriptions.COMPANY_INVOICE, Projection.FULL));
        dispatch(fetchEntities(EntityDescriptions.COMPANY));
    }, [dispatch]);

    const companyInvoices = useSelector((state: StoreState) =>
        getEntities<CompanyInvoiceFull>(state, EntityDescriptions.COMPANY_INVOICE, Projection.FULL),
    );
    const companies = useSelector((state: StoreState) => getEntities<Company>(state, EntityDescriptions.COMPANY));

    const monthMenuItems = useMemo(() => {
        const current = moment().add(1, 'month').startOf('month');
        const endPoint = moment('2019-02-01').startOf('month');
        const items = [{ text: '-', value: '-' }];
        while (current.isSameOrAfter(endPoint)) {
            items.push({
                text: current.format('MMMM YYYY'),
                value: current.format('YYYY-MM-DD')
            });

            current.subtract(1, 'month');
        }
        return items;
    }, []);

    const handleDataFetched = useCallback((data: SupportHoursInvoiceItem[]) => {
        setSupportHours(data);
    }, []);

    const isCurrentMonth = (monthValue?: string) => {
        if (!monthValue || monthValue === '-') return false;
        return moment(monthValue).isSame(moment(), 'month');
    };

    const restrictToCurrentMonth = () => ({
        minDate: moment().startOf('month').toDate(),
        maxDate: moment().endOf('month').toDate(),
    });

    const createSupportHours = async (values: SupportHoursInvoiceItemFormFields, form: any) => {
        await callApi(`${ENV.apiUrl}support-hours-invoice-items/create`, HttpMethod.POST, values).then(() => {
            setShowSuccess(true);
            form.change('supportTaskDescription', undefined);
            form.change('supportStarted', undefined);
            form.change('supportEnded', undefined);
            form.resetFieldState('supportTaskDescription');
            form.resetFieldState('supportStarted');
            form.resetFieldState('supportEnded');
        });

        callApi(
            `${ENV.apiUrl}support-hours-invoice-items/getOperatorSupportInvoiceItems?companyId=${values.company}&monthYear=${values.month}`,
            HttpMethod.GET,
        ).then(result => {
            handleDataFetched(result?.response || []);
        });
    };
    const formatDateTable = (date: string) => {
        return moment(date).format("DD-MM-YYYY HH:mm:ss").toString()
    }

    const renderDeleteEntry = (value: any, data?: SupportHoursInvoiceItem) => {
        const startMoment = moment(data!!.supportStarted);
        const isSameMonth = startMoment.isSame(moment(), 'month');
        const isSameYear = startMoment.isSame(moment(), 'year');
        if(!isSameYear || !isSameMonth) return;

        return <Icon name={'delete'} onClick={()=>{
            setSelectedSupportHourInvoiceItem(data)
            confirmSingleDialog.open()
        }} />
    }

    const cols: DataTableCol[] = [
        { dataKey: 'supportTaskDescription', name: t('entities:supportHours.description.label') },
        { dataKey: 'supportStarted', name: t('entities:supportHours.start.label'),format: formatDateTable  },
        { dataKey: 'supportEnded', name: t('entities:supportHours.end.label'),format: formatDateTable },
        {
            dataKey: 'sendSingleCompanyInvoice',
            name: t('entities:supportHours.actions.label'),
            format: renderDeleteEntry,
        },
    ];

    const confirmSingleDialog = useDisclosure();

    const validate = (values: any) => {
        const errors: any = {};
        if (!values.company) errors.company = t('required');
        if (!values.month || values.month === '-') errors.month = t('required');

        if (values.month && values.month !== '-') {
            if (!values.supportTaskDescription) errors.supportTaskDescription = t('required');
            if (!values.supportStarted) errors.supportStarted = t('required');
            if (!values.supportEnded) {
                errors.supportEnded = t('required');
            } else if (values.supportStarted && moment(values.supportEnded).isBefore(moment(values.supportStarted))) {
                errors.supportEnded = t('support_hours.errors.end_before_start');
            }
        }

        const currentMonth = moment(values.month)
        const start = moment(values.supportStarted)
        const end = moment(values.supportEnded)
        if(!currentMonth.isSame(start,"month") || !currentMonth.isSame(start,"year")) errors.supportStarted =t("support_hours.errors.same_billing_period")
        if(!currentMonth.isSame(end,"month") || !currentMonth.isSame(end,"year")) errors.supportEnded = t("support_hours.errors.same_billing_period")

        return errors;
    };

    return (
        <Segment basic>
            <FinalForm
                onSubmit={createSupportHours}
                validate={validate}
                render={({ handleSubmit, form, values }) => (
                    <Form onSubmit={handleSubmit} noValidate>
                        <SupportHoursFetcher
                            company={values.company}
                            month={values.month}
                            onFetched={handleDataFetched}
                        />

                        <Form.Group widths={2}>
                            <Field
                                name="company"
                                label={t('entities:supportHours.company.label')}
                                component={SelectField}
                                options={companies.items.map(c => ({ text: c.name, value: c.id }))}
                                required
                            />
                            <Field
                                name="month"
                                label={t('entities:supportHours.month.label')}
                                component={SelectField}
                                options={monthMenuItems}
                                required
                            />
                        </Form.Group>

                        {values.company && values.month && values.month !== '-' && isCurrentMonth(values.month) && (
                            <>
                                <Form.Group widths="equal" style={{ alignItems: 'flex-end', marginTop: 20 }}>
                                    <Field
                                        name="supportTaskDescription"
                                        label={t('entities:supportHours.description.label')}
                                        component={SimpleInput}
                                        required
                                    />
                                    <Field
                                        name="supportStarted"
                                        label={t('entities:supportHours.start.label')}
                                        component={DateTimeInput}
                                        required
                                        {...restrictToCurrentMonth()}
                                        onChange={(value: Date) => {
                                            const m = moment(value);
                                            form.change('supportStarted', m.toDate());
                                            form.change('supportEnded', m.clone().add(30, 'minutes').toDate());
                                        }}
                                    />
                                    <Field
                                        name="supportEnded"
                                        label={t('entities:supportHours.end.label')}
                                        component={DateTimeInput}
                                        required
                                        {...restrictToCurrentMonth()}
                                    />
                                    <Form.Field>
                                        <label>{t('entities:supportHours.duration.label')}</label>
                                        <div style={{ fontWeight: 'bold' }}>
                                            {(():any => {
                                                const start = values.supportStarted as any;
                                                const end = values.supportEnded as any;
                                                if (start && end) {
                                                    const mStart = moment(start);
                                                    const mEnd = moment(end);
                                                    if (mStart.isValid() && mEnd.isValid()) {
                                                        const diff = mEnd.diff(mStart, 'minutes');
                                                        return diff >= 0 ? `${Math.floor(diff / 60)}h ${diff % 60}m` : '-';
                                                    }
                                                }
                                                return '-';
                                            })()}
                                        </div>
                                    </Form.Field>
                                    <Form.Field>
                                        <button type="submit" className="ui primary button">
                                            {t('support_hours.add')}
                                        </button>
                                    </Form.Field>
                                </Form.Group>
                                <Dialog
                                    variant="confirm"
                                    {...confirmSingleDialog}
                                    actions={{
                                        ok: () => {

                                            callApi(`${ENV.apiUrl}${EntityDescriptions.SUPPORT_HOURS.repository}/${selectedSupportHourInvoiceItem!!.id}/delete`, HttpMethod.DELETE).then(r => {

                                                setShowSuccess(true)
                                                callApi(
                                                    `${ENV.apiUrl}support-hours-invoice-items/getOperatorSupportInvoiceItems?companyId=${values.company}&monthYear=${values.month}`,
                                                    HttpMethod.GET,
                                                ).then(result => {
                                                    handleDataFetched(result?.response || []);
                                                });
                                                setSelectedSupportHourInvoiceItem(undefined)
                                            }).catch((error)=>{
                                                setShowError(true)
                                            });
                                        },
                                    }}
                                    title={t('support_hours.dialog.title')}>
                                    {t('support_hours.dialog.content')}
                                </Dialog>
                            </>
                        )}
                        {supportHours && (

                            <div style={{ marginTop: 30 }}>
                                <Header
                                    as="h2"
                                    textAlign="left"
                                    content={t('support_hours.support_hours_table',{month: moment(values.month).format("MMM YY")})}
                                />
                                <DataTable
                                    t={t}
                                    sortable
                                    editable
                                    rows={supportHours}
                                    cols={cols}
                                />
                            </div>
                        )}

                    </Form>
                )}


            />

            <Portal open={showSuccess} onClose={() => setShowSuccess(false)}>
                <Message
                    success
                    onDismiss={() => setShowSuccess(false)}
                    header={t('success')}
                    content={t('support_hours.action.success')}
                    style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 1000 }}
                />
            </Portal>

            <Portal open={showError} onClose={() => setShowError(false)}>
                <Message
                    error
                    onDismiss={() => setShowError(false)}
                    header={t('success')}
                    content={t('support_hours.action.error')}
                    style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 1000 }}
                />
            </Portal>
        </Segment>
    );
};